import { ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Modal, useModalClose } from './Modal'

export interface ConfirmOptions {
  title: string
  body?: ReactNode
  confirmLabel: string
  cancelLabel?: string
  danger?: boolean
  onConfirm: () => void
}

/** In-app replacement for window.confirm(). */
export function ConfirmDialog({
  options,
  onClose,
}: {
  options: ConfirmOptions
  onClose: () => void
}) {
  return (
    <Modal onClose={onClose} label={options.title} maxWidth={440}>
      <ConfirmBody options={options} />
    </Modal>
  )
}

function ConfirmBody({ options }: { options: ConfirmOptions }) {
  const close = useModalClose()
  return (
    <div className="pt-1">
      <div className="flex items-start gap-4">
        <span
          className={`grid h-11 w-11 flex-none place-items-center rounded-2xl ${
            options.danger
              ? 'bg-[rgba(255,120,150,0.12)] text-[#FFA3B4]'
              : 'bg-[rgba(165,139,255,0.16)] text-vault-300'
          }`}
          aria-hidden="true"
        >
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-xl font-semibold tracking-tight">
            {options.title}
          </h2>
          {options.body && (
            <div className="mt-1.5 text-muted">{options.body}</div>
          )}
        </div>
      </div>
      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" className="btn-secondary" onClick={close}>
          {options.cancelLabel ?? 'Cancel'}
        </button>
        <button
          type="button"
          autoFocus
          className={options.danger ? 'btn-danger' : 'btn-primary'}
          onClick={() => {
            options.onConfirm()
            close()
          }}
        >
          {options.confirmLabel}
        </button>
      </div>
    </div>
  )
}
